// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/** Shared shapes for the GGUF parser. Kept dependency-free so the worker, the
 *  main thread and the test suite all agree on one vocabulary. */

/** GGUF metadata value type tags, as written in the file (a u32 after each key). */
export enum GgufValueType {
  UINT8 = 0,
  INT8 = 1,
  UINT16 = 2,
  INT16 = 3,
  UINT32 = 4,
  INT32 = 5,
  FLOAT32 = 6,
  BOOL = 7,
  STRING = 8,
  ARRAY = 9,
  UINT64 = 10,
  INT64 = 11,
  FLOAT64 = 12,
}

/**
 * A metadata value as we keep it.
 *
 * Large arrays (the tokenizer's 150k-entry token list, its merges, its token
 * types) are deliberately NOT materialised — we walk their length prefixes to
 * find the end and record only a summary. Building 150k JS strings to then
 * throw them away is the difference between a 40 ms parse and a janked tab.
 */
export type GgufValue =
  | { kind: 'scalar'; type: GgufValueType; value: number | boolean | bigint }
  | { kind: 'string'; value: string }
  | {
      kind: 'array';
      elemType: GgufValueType;
      count: number;
      values: unknown[] | null;
      /**
       * Absolute byte offset of the first element. Recorded so a skipped array
       * can still be indexed into later — resolving the two token strings a
       * chat template needs, without ever building the other 151 000.
       */
      elemStart: number;
    };

export interface GgufTensorInfo {
  name: string;
  dims: number[];
  /** Raw ggml_type id as written in the file. */
  typeId: number;
  /** Byte offset from the start of the tensor-data section (not the file). */
  offset: number;
  /** Product of dims — the element count. */
  elements: number;
}

export interface GgufHeader {
  version: number;
  littleEndian: boolean;
  tensorCount: number;
  kvCount: number;
}

export interface ParsedGguf {
  header: GgufHeader;
  /** Flat metadata map. Values follow GgufValue; huge arrays are summarised. */
  kv: Map<string, GgufValue>;
  tensors: GgufTensorInfo[];
  /** Byte offset where the KV block ended. */
  kvBlockEnd: number;
  /** Byte offset where the tensor-info block ended. */
  tensorInfoEnd: number;
  /** general.alignment (default 32). */
  alignment: number;
  /** First byte of the tensor data section (tensorInfoEnd rounded up to alignment). */
  dataStart: number;
  /** How many bytes we actually had to read to get here. */
  bytesRead: number;
}

/** Thrown when the buffer ran out mid-parse — the caller should fetch more. */
export class NeedMoreBytes extends Error {
  constructor(public readonly neededAtLeast: number) {
    super(`need more bytes (at least ${neededAtLeast})`);
    this.name = 'NeedMoreBytes';
  }
}

/** Thrown when the bytes are not a GGUF file at all. */
export class NotGguf extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotGguf';
  }
}
